import { useEffect, useMemo, useRef, useState } from 'react'
import { ActionIcon, Alert, Tooltip } from '@mantine/core'
import { IconAlertTriangle, IconPin, IconPinnedFilled, IconRefresh } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { usePreviewBridge } from '../../hooks/usePreviewBridge'
import { PreviewStatus } from './PreviewStatus'
import { PUBLISHER_HEADER_SRC } from './publisherHeader'
import classes from './PreviewStage.module.css'

// Figma frame sizes: the halfpage ad is drawn at 280×560 inside the 300px screen.
const HALFPAGE_SCALE = 280 / 300
const SKELETON_BLOCKS = [86, 26, 26, 52]

function presentationFor(format) {
  const width = Number(format?.width || 300)
  const height = Number(format?.height || 250)
  if (width >= 728 && height <= 300) return 'billboard'
  if (height > width * 1.5) return 'halfpage'
  return 'phone'
}

/**
 * The live preview of the AD-209 editor. One sandboxed iframe runs the real template
 * runtime in every presentation — the phone mock (medium rectangle / halfpage), the
 * billboard strip, and Step 1 where the placeholder ad covers it — and it keeps its place
 * in the tree through all of them, so switching format or leaving Step 1 never reloads it.
 *
 * `restartRef.current` exposes the runtime restart for the header's "Refresh Ad".
 * `overlay` (the Ad Agent card) is drawn where Figma places it: over the lower half of the
 * phone, sticking out 76px to its right.
 */
export function PreviewStage({
  previewKey,
  previewUrl,
  configuration,
  selectedSlide,
  onSelectSlide,
  selectedItems,
  onSelectItem,
  protocolVersion,
  onStatusChange,
  placeholder = false,
  pinned = false,
  onTogglePin,
  restartRef,
  overlay = null,
}) {
  const { t } = useTranslation()
  const iframeRef = useRef(null)
  const stripRef = useRef(null)
  const phoneRef = useRef(null)
  const [stripWidth, setStripWidth] = useState(970)
  const [phoneScale, setPhoneScale] = useState(1)
  const format = configuration?.format || { width: 300, height: 250 }
  const width = Number(format.width || 300)
  const height = Number(format.height || 250)
  const mode = presentationFor(format)
  const src = useMemo(() => previewUrl || null, [previewUrl])

  const bridge = usePreviewBridge({
    iframeRef,
    templateId: previewKey,
    configuration,
    selectedSlide,
    onSelectSlide,
    selectedItems,
    onSelectItem,
    protocolVersion,
  })

  useEffect(() => {
    onStatusChange?.({
      status: bridge.status,
      error: bridge.error,
      diagnostics: bridge.diagnostics,
      configuration: bridge.diagnosticConfiguration,
      checked: bridge.diagnosticConfiguration === configuration,
    })
  }, [bridge.error, bridge.status, bridge.diagnostics, bridge.diagnosticConfiguration, configuration, onStatusChange])

  useEffect(() => {
    if (restartRef) restartRef.current = bridge.restart
  }, [bridge.restart, restartRef])

  // The billboard strip scales down only when the page is narrower than the ad.
  useEffect(() => {
    const node = stripRef.current
    if (!node || mode !== 'billboard') return undefined
    const measure = () => setStripWidth(node.clientWidth || 970)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [mode])

  // The phone is sticky: on a short window shrink the whole device (the ad still renders at
  // its real pixel size inside) so its lower half stays reachable.
  useEffect(() => {
    if (mode === 'billboard') return undefined
    const fit = () => {
      const offset = 70 + 64 + 16 + 36 + 16 + 16
      const available = window.innerHeight - offset
      const next = available > 0 ? Math.min(1, Math.max(0.45, available / 654)) : 1
      setPhoneScale((current) => (Math.abs(current - next) < 0.005 ? current : next))
    }
    fit()
    window.addEventListener('resize', fit)
    return () => window.removeEventListener('resize', fit)
  }, [mode])

  let scale = 1
  if (mode === 'phone') scale = Math.min(1, 300 / width)
  if (mode === 'halfpage') scale = Math.min(HALFPAGE_SCALE, 560 / height)
  if (mode === 'billboard') scale = Math.min(1, (stripWidth || width) / width)
  const slotWidth = Math.round(width * scale)
  const slotHeight = Math.round(height * scale)
  const unhealthy = bridge.status === 'error' || Boolean(bridge.error)
  const isBillboard = mode === 'billboard'
  const scaledPhone = !isBillboard && phoneScale < 1

  return (
    <div className={classes.stage} data-mode={mode}>
      {(unhealthy || (bridge.status === 'loading' && !placeholder)) && (
        <div className={classes.status}><PreviewStatus status={bridge.status} /></div>
      )}
      {bridge.error && (
        <Alert color="red" icon={<IconAlertTriangle size={17} />} title={bridge.error.phase} mb="xs">
          {bridge.error.message}
        </Alert>
      )}

      <div
        className={isBillboard ? undefined : classes.fit}
        style={scaledPhone ? { height: Math.ceil(654 * phoneScale) } : (isBillboard ? { width: '100%' } : undefined)}
      >
        <div
          ref={isBillboard ? stripRef : phoneRef}
          className={isBillboard ? classes.strip : classes.phone}
          style={scaledPhone ? { transform: `scale(${phoneScale})` } : undefined}
        >
          <div className={isBillboard ? undefined : classes.screen}>
            {mode === 'phone' && <img className={classes.publisher} src={PUBLISHER_HEADER_SRC} alt="" />}
            {mode === 'phone' && <span className={classes.addressBar} aria-hidden="true" />}
            <div
              className={isBillboard ? `${classes.slot} ${classes.stripSlot}` : classes.slot}
              style={{ width: slotWidth, height: slotHeight }}
            >
              {src && (
                <iframe
                  ref={iframeRef}
                  src={src}
                  title={t('editorV2.shell.preview.frameTitle')}
                  width={slotWidth}
                  height={slotHeight}
                  sandbox="allow-scripts"
                  referrerPolicy="no-referrer"
                  onLoad={bridge.onFrameLoad}
                  style={{ width: slotWidth, height: slotHeight, position: 'relative', zIndex: 1 }}
                  aria-hidden={placeholder ? 'true' : undefined}
                  tabIndex={placeholder ? -1 : undefined}
                />
              )}
              {placeholder && (
                <div className={classes.placeholder} aria-label={t('editorV2.shell.preview.placeholder')}>
                  <span className={classes.placeholderImage} />
                  <span className={classes.placeholderContent}>
                    <span className={classes.placeholderBrand}><span /></span>
                    <span style={{ width: 108 }} />
                    <span style={{ width: 124 }} />
                    <span style={{ width: 124 }} />
                    <span style={{ width: 52, marginTop: 10 }} />
                    <span className={classes.placeholderDot} />
                  </span>
                </div>
              )}
            </div>
            {mode === 'phone' && (
              <div className={classes.skeleton} aria-hidden="true">
                {SKELETON_BLOCKS.map((blockHeight, index) => (
                  <span key={index} style={{ height: blockHeight }} />
                ))}
              </div>
            )}
            {!isBillboard && <span className={classes.home} aria-hidden="true" />}
          </div>

          {isBillboard && onTogglePin && (
            <Tooltip label={pinned ? t('editorV2.shell.preview.unpin') : t('editorV2.shell.preview.pin')} withArrow>
              <button
                type="button"
                className={classes.pin}
                data-pinned={pinned ? 'true' : 'false'}
                aria-pressed={pinned}
                aria-label={pinned ? t('editorV2.shell.preview.unpin') : t('editorV2.shell.preview.pin')}
                onClick={onTogglePin}
              >
                {pinned ? <IconPinnedFilled size={16} /> : <IconPin size={16} stroke={1.5} />}
              </button>
            </Tooltip>
          )}
        </div>
        {overlay && (
          <div
            className={classes.overlay}
            style={isBillboard ? undefined : { top: Math.round(401 * (scaledPhone ? phoneScale : 1)) }}
          >
            {overlay}
          </div>
        )}
      </div>

      {!placeholder && unhealthy && (
        <Tooltip label={t('editorV2.shell.preview.restart')} withArrow>
          <ActionIcon
            variant="default"
            size="md"
            radius="xl"
            mt="xs"
            aria-label={t('editorV2.shell.preview.restart')}
            onClick={bridge.restart}
          >
            <IconRefresh size={15} />
          </ActionIcon>
        </Tooltip>
      )}
    </div>
  )
}

export { presentationFor }
